import { EditorModes, hasSomeLayoutRuleConfigInMap, } from "@buildwithflux/models";
import { createActionRecordAction } from "../actionCreator";
import { createAction } from "../actionUtils";
export const updateDescription = createActionRecordAction("updateDescription", (description) => {
    return {
        payload: {
            description,
            canUndo: true,
            updatesDocumentTimestamp: true,
            shouldGenerateActionRecord: true,
        },
    };
}, "updated the document description");
export const addElements = createActionRecordAction("addElements", (params, editorMode) => {
    const { elements, dropPosition, selectElements } = params;
    return {
        payload: {
            elements,
            selectElements,
            dropPosition,
            genesisContainerType: params.genesisContainerType,
            inPcbEditor: editorMode === EditorModes.pcb,
            canUndo: true,
            updatesDocumentTimestamp: true,
            shouldGenerateActionRecord: true,
        },
    };
}, "added elements");
// Note re `deselectUids`: deleteSubjects does do not attempt to automatically deselect the deleted
// subjectUids. Instead, we require the caller to pass in the full set of of uids to deselect
// separately. This is because the subjects to deselect usually also include other subjects that
// aren't explicitly deleted but will no longer exist -- eg virtual subjects like wire segments or
// generated subjects like nets. It is the caller that has sufficient context to know those things
// (eg `SceneStateManager.getRelatedSubjectsToDeselect`).
//
// We could combine the deleted subjects with the "also deselect" subjects before deselecting, like
// we used to, but it actually just makes the interface more likely to be misused, with the caller
// not specifying all the right things and things appearing to work but being non-robust. Since the
// caller has to calculate the full set anyway, better to just require it.
export const deleteSubjects = createActionRecordAction("deleteSubjects", (subjectUids, vertexUpdates, routeUpdates, deselectUids = [], subscriptionKey) => {
    return {
        payload: {
            subjectUids,
            deselectUids,
            vertexUpdates,
            routeUpdates,
            subscriptionKey,
            canUndo: true,
            updatesDocumentTimestamp: true,
            shouldGenerateActionRecord: true,
        },
    };
}, "deleted components");
/**
 * NOTE: this is an exceptional action that can be used as a system action or
 * action record action
 */
export const updateElementsLabel = createAction("updateElementsLabel", (elements, canUndo = true) => {
    return {
        payload: {
            elements,
            canUndo,
            updatesDocumentTimestamp: canUndo,
            shouldGenerateActionRecord: canUndo,
        },
    };
});
export const removeSubjectProperties = createActionRecordAction("removeSubjectProperties", (propertyRemovalMap) => {
    return {
        payload: {
            propertyRemovalMap,
            canUndo: true,
            updatesDocumentTimestamp: true,
            shouldGenerateActionRecord: true,
        },
    };
}, "removed properties from component");
export const setSubjectProperties = createActionRecordAction("setSubjectProperties", (propertyUpdateMap, subscriptionKey) => {
    return {
        payload: {
            propertyUpdateMap,
            subscriptionKey,
            canUndo: true,
            updatesDocumentTimestamp: true,
            shouldGenerateActionRecord: true,
        },
    };
}, "added or updated properties for components");
export const setDocumentProperties = createActionRecordAction("setDocumentProperties", (setProperties) => {
    return {
        payload: {
            setProperties,
            canUndo: true,
            updatesDocumentTimestamp: true,
            shouldGenerateActionRecord: true,
        },
    };
}, "added or updated project properties");
export const removeDocumentProperties = createActionRecordAction("removeDocumentProperties", (propertyUids) => {
    return {
        payload: {
            propertyUids,
            canUndo: true,
            updatesDocumentTimestamp: true,
            shouldGenerateActionRecord: true,
        },
    };
}, "removed project properties");
export const setNodeRules = createActionRecordAction("setNodeRules", (updatedNodesRulesSetsMap) => {
    const hasInheritedRules = hasSomeLayoutRuleConfigInMap(updatedNodesRulesSetsMap, "allChildNodesWillInheritThisRule");
    const hasInheritedCalculations = hasSomeLayoutRuleConfigInMap(updatedNodesRulesSetsMap, "affectsDescendantCalculations");
    const requiresDescendantRebake = hasSomeLayoutRuleConfigInMap(updatedNodesRulesSetsMap, "requiresDescendantRebake");
    const requiresNetDescendantRebake = hasSomeLayoutRuleConfigInMap(updatedNodesRulesSetsMap, "requiresNetDescendantRebake");
    return {
        payload: {
            // TODO: rename updatedNodes
            updatedNodes: updatedNodesRulesSetsMap,
            canUndo: true,
            updatesDocumentTimestamp: true,
            shouldGenerateActionRecord: true,
            hasInheritedRules,
            hasInheritedCalculations,
            requiresDescendantRebake,
            requiresNetDescendantRebake,
        },
    };
}, "set object-specific pcb layout rules");
/**
 * Sets document configs for the current user.
 * This action does NOT generate action records and does NOT update the document timestamp.
 * The currentUser is injected by the DocumentDispatcher/middleware, not passed as a parameter.
 * This action is NOT undoable.
 */
export const setDocumentConfigs = createAction("setDocumentConfigs", (configs) => {
    return {
        payload: {
            configs,
            canUndo: false,
            updatesDocumentTimestamp: false,
            shouldGenerateActionRecord: false,
        },
    };
});
/**
 * Sets the value of a simulation control for an element.
 * This action does NOT generate action records and does NOT update the document timestamp.
 * The currentUser is injected by the DocumentDispatcher/middleware, not passed as a parameter.
 */
export const setSimulationControlsValue = createAction("setSimulationControlsValue", (elementUid, controlUid, value) => {
    return {
        payload: {
            elementUid,
            controlUid,
            value,
            canUndo: true,
            updatesDocumentTimestamp: false,
            shouldGenerateActionRecord: false,
        },
    };
});
//# sourceMappingURL=actions.js.map